import { FetchResult, useMutation } from '@apollo/client'
import {
  SurveyOrderStatus,
  SurveyState,
  UpdateSurveyOrderMutationVariables
} from '../../data/gql-gen/dashboard/graphql'
import {
  UPDATE_SURVEY_ORDER,
  UpdateSurveyOrderData
} from '../../data/gql/order/mutations/updateSurveyOrder'
import { LoggerErrorType } from '../../data/logger'
import { captureApolloError } from '../../utils/HelperFunctions'
import { useUpdateSurvey } from './Survey.hooks'

// The mutations swallow errors via `onError` (so the promises resolve), which
// means failures surface only on the resolved result. With graphQL errors the
// result carries `GraphQLError[]`; on network errors Apollo resolves with the
// `ApolloError` itself in `errors` — normalise both to messages.
const getErrorMessages = (result: FetchResult | undefined): string[] => {
  const errors = result?.errors
  if (!errors) return []
  if (Array.isArray(errors)) return errors.map((error) => error.message)
  return [(errors as { message?: string }).message ?? String(errors)]
}

// "An order for survey ID '…' could not be found." — the survey was never
// fielded through the ordering flow, so there is no supplier order to update.
const isOrderNotFound = (message: string) => {
  const normalised = message.toLowerCase()
  return normalised.includes('order') && normalised.includes('not be found')
}

/**
 * Pause / resume / end a single survey by id. Mirrors the `PauseProject` and
 * `EndProject` flows: flip the panel-supplier order status first, then the
 * dashboard survey state. Used per market on the Confirm page and the results
 * page (PauseProject/EndProject themselves are hard-wired to
 * `selectedProject.surveys[0]`). `onCompleted` is run after the mutations so
 * the caller can refetch the project and refresh the status badge.
 *
 * Surveys without an order (never fielded through the ordering flow) have no
 * supplier order to update, so that specific failure falls through to the
 * survey-state update; any other order failure aborts it so the dashboard
 * state can't diverge from a still-live supplier order.
 *
 * Each action resolves to whether the state change went through, so callers
 * can surface failures — the mutations swallow their errors via onError, so
 * nothing throws.
 */
export const usePauseResumeSurvey = (
  onCompleted?: () => void | Promise<unknown>
) => {
  const { updateSurvey } = useUpdateSurvey()
  const [updateSurveyOrder, { loading }] = useMutation<
    UpdateSurveyOrderData,
    UpdateSurveyOrderMutationVariables
  >(UPDATE_SURVEY_ORDER, {
    context: { clientName: 'dashboard' },
    onError: (error) => {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'updateSurveyOrder',
        error
      )
    }
  })

  const setStatus = async (
    surveyId: string,
    orderStatus: SurveyOrderStatus,
    state: SurveyState
  ): Promise<boolean> => {
    const orderResult = await updateSurveyOrder({
      variables: {
        surveyId,
        orderStatus
      }
    })
    const orderFailures = getErrorMessages(orderResult).filter(
      (message) => !isOrderNotFound(message)
    )
    let succeeded = false
    if (orderFailures.length === 0) {
      const surveyResult = await updateSurvey({
        variables: {
          surveyId,
          state
        }
      })
      succeeded = getErrorMessages(surveyResult).length === 0
    }
    // Refresh even after a failed update so the UI resyncs with the server.
    await onCompleted?.()
    return succeeded
  }

  return {
    pause: (surveyId: string) =>
      setStatus(surveyId, SurveyOrderStatus.Paused, SurveyState.Paused),
    resume: (surveyId: string) =>
      setStatus(surveyId, SurveyOrderStatus.Live, SurveyState.Live),
    end: (surveyId: string) =>
      setStatus(surveyId, SurveyOrderStatus.Completed, SurveyState.Completed),
    loading
  }
}

export default usePauseResumeSurvey
